import React, { Dispatch, FC, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { scrollToViewById } from 'src/modules/shared/helpers/scroll';
import { useAppDispatch, useAppSelector } from 'src/modules/shared/hooks/useAppDispatch';
import {
  setActiveItem,
  setNavigationBarStatus,
} from 'src/modules/shared/slices/navigationBar/navigationBarSlice';
import { NavbarContent } from 'src/modules/shared/slices/navigationBar/type';
import { useGetTableOfContentQuery } from 'src/store/table-of-contents/tableOfContentsApi';
import { TableOfContentsChapter } from 'src/store/table-of-contents/types';
import { CustomSpan } from './TableOfContentesNavBarInfoStyled';
import {
  setClickedOnNavgationBarIndex,
  setExpandedIndex,
} from 'src/store/table-of-contents/tableOfContentsSlice';

export const TableOfContentsNavBarInfo: FC<{
  setContentV2: Dispatch<SetStateAction<NavbarContent[] | null>>;
}> = ({ setContentV2 }) => {
  const dispatch = useAppDispatch();
  const { activeItem } = useAppSelector((state) => state.navigationBar);
  const bookId = useAppSelector((state) => state.books.bookId);
  const expandedIndex = useAppSelector((state) => state.tableOfContents.expandedIndex);
  const { data } = useGetTableOfContentQuery(bookId ?? '', {
    skip: !bookId,
  });

  const handleChapterClick = useCallback(
    (id: string) => {
      if (expandedIndex) {
        dispatch(setExpandedIndex(''));
      }
      dispatch(setClickedOnNavgationBarIndex(id));
      setTimeout(() => {
        scrollToViewById(`chapter-${id}`);
      }, 500);
    },
    [dispatch, expandedIndex]
  );

  useEffect(() => {
    if (expandedIndex) {
      dispatch(setActiveItem(expandedIndex));
    } else if (activeItem !== '') {
      dispatch(setActiveItem(''));
    }
  }, [activeItem, dispatch, expandedIndex]);

  const navigationContent: NavbarContent[] | null = useMemo(() => {
    if (!data) return null;

    const chapterContent = data?.chapters.map((item: TableOfContentsChapter) => {
      return {
        type: 'chapters',
        id: item.id,
        firstItem: item.number,
        secondItem: item.title,
        onClick: () => handleChapterClick(item.id),
        activecolor: item.color,
      };
    });
    const bookInfo: NavbarContent = {
      type: 'book',
      id: data.id,
      firstItem: (
        <>
          <CustomSpan>
            <img
              alt={`book cover for method ${data.title}`}
              src={data.coverImage}
              width={24}
              height={24}
              style={{ borderRadius: 4 }}
            />
          </CustomSpan>
        </>
      ),
      secondItem: (
        <>
          <CustomSpan>{data.title}</CustomSpan>
        </>
      ),
      onClick: () => {},
      activecolor: '',
    };
    const content = [bookInfo, ...chapterContent];

    return content;
  }, [data, handleChapterClick]);

  useEffect(() => {
    if (!navigationContent) return;
    setContentV2(navigationContent);
  }, [navigationContent, setContentV2]);

  useEffect(() => {
    dispatch(setNavigationBarStatus(true));
  }, [dispatch]);

  useEffect(
    () => () => {
      setContentV2(null);
    },
    [dispatch, setContentV2]
  );

  return null;
};
